import React from 'react';
import ConfirmButton, { IConfirmButtonProps } from '@src/emp-3.0/components/common/ConfirmButton';
import { Checkbox, CheckboxProps, Tooltip } from 'antd';
import Icon, { InfoCircleOutlined } from '@ant-design/icons';
import { KeepHistoricalWarningMessage } from '@src/pages/workflow/components/FlowBuilder/components/index';
import DeployIcon from '@icons/emp/deploy.svg';
import { useInitialInfoStore } from '@src/emp-3.0/store/useInitialInfo';

interface IProps {
  type: 'flow' | 'metric';
  showKeepHistoricalData?: boolean;
  checkboxProps?: CheckboxProps;
  cancelButtonProps?: IConfirmButtonProps;
  saveButtonProps?: IConfirmButtonProps;
  deployButtonProps?: IConfirmButtonProps;
}

const Footer: React.FC<IProps> = ({
  type,
  showKeepHistoricalData = false,
  cancelButtonProps,
  saveButtonProps,
  deployButtonProps,
  checkboxProps,
}) => {
  const keepHistoricalData = !!checkboxProps?.checked;
  const isFlow = type === 'flow';
  const { workbenchMode } = useInitialInfoStore((state) => state);
  const cancelButton = (
    <ConfirmButton
      confirmProps={{
        title: null,
        icon: null,
        description: (
          <div style={{ marginRight: '22px' }}>
            <div>Are you sure you want to cancel and exit Metric</div>
            <div>Builder? Unsaved changes will be lost.</div>
          </div>
        ),
        placement: 'topLeft',
        okText: 'Exit',
        okButtonProps: { type: 'default' },
        cancelButtonProps: { type: 'primary', ghost: true },
        cancelText: 'No',
      }}
      {...cancelButtonProps}
    >
      Exit
    </ConfirmButton>
  );

  const checkboxDom = (
    <Checkbox {...checkboxProps}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: 'rgba(0, 0, 0, 0.88)',
        }}
      >
        Keep historical data
        <Tooltip
          title={`This option determines how to handle historical data. Selecting 'Keep historical data' lets you modify incoming traffic while preserving historical context. Unchecking this option will reset your ${
            isFlow ? 'flow' : ''
          } metrics and remove historical data.`}
        >
          <InfoCircleOutlined />
        </Tooltip>
      </div>
    </Checkbox>
  );
  // 2025.04.08
  // Always show keep historical data checkbox.
  // If only the flow name is changed, hide the double confirm popup.
  // 2025.04.10
  // If keepHistoricalData is false, show the double confirm popup.
  const saveButton = (
    <ConfirmButton
      type={'primary'}
      ghost
      {...saveButtonProps}
      confirmProps={{
        title: null,
        icon: false,
        description: <KeepHistoricalWarningMessage type={type} keep={keepHistoricalData} />,
        okText: 'Confirm',
        okButtonProps: { type: 'primary', danger: !keepHistoricalData },
        ...saveButtonProps?.confirmProps,
      }}
    >
      Save
    </ConfirmButton>
  );

  const deployButton = !workbenchMode && (
    <ConfirmButton
      type={'primary'}
      icon={<Icon component={DeployIcon} />}
      {...deployButtonProps}
      confirmProps={{
        title: null,
        icon: false,
        description: <KeepHistoricalWarningMessage keep={keepHistoricalData} />,
        okText: 'Confirm',
        okButtonProps: { type: 'primary', danger: !keepHistoricalData },
        ...deployButtonProps?.confirmProps,
      }}
    >
      Deploy
    </ConfirmButton>
  );

  return (
    <div
      style={{
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '8px',
      }}
    >
      <div>{cancelButton}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {showKeepHistoricalData && checkboxDom}
        {saveButton}
        {deployButton}
      </div>
    </div>
  );
};

export default Footer;
